import { Row, Col, FormRadioGroup, InPageAlert, FormInput } from '@snsw/react-component-library';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';
import { RefData } from 'plantItemNew/constants/refData';
import React, { useEffect, useState } from 'react';
import { sanitizeHtml } from 'shared/utils/Helpers';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import styles from '../PlantItemDetails.module.scss';

export const PressureVesselDetails = React.forwardRef((props, ref) => {
  const { value = {}, onChange = () => {}, refData = {}, onErrorChange = () => {} } = props;

  const {
    numberOfChambers: savedNumberOfChambers = '',
    hazardLevel: savedHazardLevel = '',
    designPressure: savedDesignPressure = '',
    volume: savedVolume = '',
    minDesignTemp: savedMinDesignTemp = '',
    maxDesignTemp: savedMaxDesignTemp = '',
    chamberContents: savedChamberContents = '',
  } = value;

  const [numberOfChambers, setNumberOfChambers] = useState(savedNumberOfChambers);
  const [hazardLevel, setHazardLevel] = useState(savedHazardLevel);
  const [designPressure, setDesignPressure] = useState(savedDesignPressure);
  const [volume, setVolume] = useState(savedVolume);
  const [minDesignTemp, setMinDesignTemp] = useState(savedMinDesignTemp);
  const [maxDesignTemp, setMaxDesignTemp] = useState(savedMaxDesignTemp);
  const [chamberContents, setChamberContents] = useState(savedChamberContents);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const validators = [
    {
      fieldName: 'numberOfChambers',
      fieldValue: numberOfChambers,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'hazardLevel',
      fieldValue: hazardLevel,
      isValid: (v) => isEmpty(numberOfChambers) || !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'volume',
      fieldValue: volume,
      isValid: (v) => isEmpty(numberOfChambers) || isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'designPressure',
      fieldValue: designPressure,
      isValid: (v) => isEmpty(numberOfChambers) || isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'minDesignTemp',
      fieldValue: minDesignTemp,
      isValid: (v) => isEmpty(numberOfChambers) || isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxDesignTemp',
      fieldValue: maxDesignTemp,
      isValid: (v) => isEmpty(numberOfChambers) || isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'chamberContents',
      fieldValue: chamberContents,
      isValid: (v) => isEmpty(numberOfChambers) || !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
  ];

  const numberOfChambersOptions = Array.isArray(refData[RefData.pressureVessel.numberOfChambersOptions])
    ? refData[RefData.pressureVessel.numberOfChambersOptions].map((e) => ({
        value: e.value,
        label: e.description,
      }))
    : [];

  const hazardLevelOptions = Array.isArray(refData[RefData.pressureVessel.hazardLevelOptions])
    ? refData[RefData.pressureVessel.hazardLevelOptions].map((e) => ({
        value: e.value,
        label: e.description,
      }))
    : [];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let details = {
      numberOfChambers,
      hazardLevel,
      designPressure,
      volume,
      minDesignTemp,
      maxDesignTemp,
      chamberContents,
    };
    onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [numberOfChambers, hazardLevel, designPressure, volume, minDesignTemp, maxDesignTemp, chamberContents]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <Row>
        <Col span={6}>
          <FormRadioGroup
            id="numberOfChambers"
            name="numberOfChambers"
            legend={PlantItemDetailsPage.relatedDetails.pressureVessel.numberOfChambersLabel}
            options={numberOfChambersOptions}
            vertical={true}
            onChange={setNumberOfChambers}
            value={numberOfChambers}
            errorMessage={errors?.numberOfChambers?.errorMessage}
            hasError={showErrors && errors?.numberOfChambers?.invalid}
          />
        </Col>
      </Row>
      {numberOfChambers && numberOfChambers !== '1' && (
        <Row>
          <Col span={6}>
            <InPageAlert variant="info" title="Help:">
              You will only need to advise the details of the most hazardous chamber in your multi-chamber pressure
              vessel
            </InPageAlert>
          </Col>
        </Row>
      )}
      {numberOfChambers && (
        <>
          <Row>
            <Col span={6}>
              <FormRadioGroup
                id="hazardLevel"
                name="hazardLevel"
                legend={PlantItemDetailsPage.relatedDetails.pressureVessel.hazardLevelLabel}
                helpMessage="Pressure vessel hazard level according to AS 4343"
                options={hazardLevelOptions}
                vertical={true}
                onChange={setHazardLevel}
                value={hazardLevel}
                errorMessage={errors?.hazardLevel?.errorMessage}
                hasError={showErrors && errors?.hazardLevel?.invalid}
              />
            </Col>
          </Row>
          <Row>
            <Col span={4}>
              <FormInput
                id="volume"
                label={PlantItemDetailsPage.relatedDetails.pressureVessel.volumeLabel}
                suffix="L"
                onChange={(e, { value }) => setVolume(value)}
                value={volume}
                errorMessage={errors?.volume?.errorMessage}
                hasError={showErrors && errors?.volume?.invalid}
                className={styles.inputWithSuffix}
              />
              <FormInput
                id="designPressure"
                label={PlantItemDetailsPage.relatedDetails.pressureVessel.designPressureLabel}
                helpMessage="kPAG = Kilo Pascal. Also referred to as KpA"
                suffix="kPag"
                onChange={(e, { value }) => setDesignPressure(value)}
                value={designPressure}
                errorMessage={errors?.designPressure?.errorMessage}
                hasError={showErrors && errors?.designPressure?.invalid}
                className={styles.inputWithSuffix}
              />
            </Col>
          </Row>
          <Row>
            <Col span={2}>
              <FormInput
                id="minDesignTemp"
                label={PlantItemDetailsPage.relatedDetails.pressureVessel.designTemperatureLabel}
                suffix={sanitizeHtml(PlantItemDetailsPage.relatedDetails.pressureVessel.minTemperatureSuffix)}
                onChange={(e, { value }) => setMinDesignTemp(value)}
                value={minDesignTemp}
                errorMessage={errors?.minDesignTemp?.errorMessage}
                hasError={showErrors && errors?.minDesignTemp?.invalid}
                className={styles.inputWithSuffix}
              />
            </Col>
            <Col span={2}>
              <FormInput
                id="maxDesignTemp"
                label="&nbsp;"
                suffix={sanitizeHtml(PlantItemDetailsPage.relatedDetails.pressureVessel.maxTemperatureSuffix)}
                onChange={(e, { value }) => setMaxDesignTemp(value)}
                value={maxDesignTemp}
                errorMessage={errors?.maxDesignTemp?.errorMessage}
                hasError={showErrors && errors?.maxDesignTemp?.invalid}
                className={styles.inputWithSuffix}
              />
            </Col>
          </Row>
          <Row>
            <Col span={9}>
              <FormInput
                id="chamberContents"
                label={PlantItemDetailsPage.relatedDetails.pressureVessel.chamberContentsLabel}
                type="textarea"
                onChange={(e, { value }) => setChamberContents(value)}
                value={chamberContents}
                errorMessage={errors?.chamberContents?.errorMessage}
                hasError={showErrors && errors?.chamberContents?.invalid}
                placeholder="e.g. LPG, ammonia, hydrogen etc and fluid phase (solid, liquid or gas)"
                maxLength={2000}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
});

PressureVesselDetails.displayName = 'PressureVesselDetails';
